import { ActionIcon, Box, Button, Stack, Table, Text, Textarea, TextInput } from '@mantine/core'
import { IconPlus, IconTrash } from '@tabler/icons-react'
import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { usePatient } from '@/hooks/usePatient'
import type { ProgressNoteEditFormValues } from '../progressNoteEditSchema'
import { MONO_FONT } from '../tokens'
import { DictateFieldMic } from '../dictation/DictateFieldMic'
import { NoteHeading } from './NoteHeading'

// Consent is guaranteed (no portal consent → no call); the identity sentence is appended only when the chart's identity-verified flag is true.
const IDENTITY_VERIFIED_TEXT = 'Patient identity verified.'
const CONSENT_BASE_TEXT = 'Patient consented to telehealth visit.'

export interface SubjectiveSectionProps {
  readOnly: boolean
  patientId?: string
}

export function SubjectiveSection({ readOnly, patientId }: SubjectiveSectionProps) {
  const { control } = useFormContext<ProgressNoteEditFormValues>()

  // Loading/absent resolves to false, so the sentence is omitted until known true.
  const identityVerified = usePatient(patientId).data?.identityVerified ?? false
  const consentText = identityVerified
    ? `${IDENTITY_VERIFIED_TEXT} ${CONSENT_BASE_TEXT}`
    : CONSENT_BASE_TEXT

  const summary = useWatch({ control, name: 'summary' })

  const rosFieldArray = useFieldArray({
    control,
    name: 'summary.ros',
  })
  const hpiFieldArray = useFieldArray({
    control,
    name: 'summary.hpi',
  })

  // Read mode hides an empty Chief Complaint (faithfulness); always editable.
  const showChiefComplaintReadOnly = readOnly && (summary?.chiefComplaint ?? '').trim() !== ''
  const hpiParagraphs = summary?.hpi ?? []
  const hpiHasContent = hpiParagraphs.some((p) => (p?.text ?? '').trim() !== '')
  const showHpi = !readOnly || hpiHasContent
  const rosRows = summary?.ros ?? []
  const showRos = !readOnly || rosRows.length > 0

  return (
    <Box mb="xl">
      <NoteHeading level="section" id="subjective" label="Subjective" />

      <Stack gap="lg">
        <Box>
          <NoteHeading level="sub" id="consent" label="Consent" />
          <Text size="sm" c="dark.7" style={{ lineHeight: 1.7 }}>
            {consentText}
          </Text>
        </Box>

        {(showChiefComplaintReadOnly || !readOnly) && (
          <Box>
            <NoteHeading level="sub" id="chief-complaint" label="Chief Complaint" />
            {readOnly ? (
              <Text size="sm" c="dark.7" style={{ lineHeight: 1.7 }}>
                {summary?.chiefComplaint || '—'}
              </Text>
            ) : (
              <Controller
                control={control}
                name="summary.chiefComplaint"
                render={({ field }) => (
                  <Textarea
                    autosize
                    minRows={2}
                    placeholder="Chief complaint"
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.currentTarget.value)}
                    onBlur={field.onBlur}
                    data-testid="subjective-chief-complaint"
                    rightSectionPointerEvents="all"
                    rightSection={
                      <DictateFieldMic
                        field={{ path: 'summary.chiefComplaint', label: 'Chief Complaint' }}
                        testId="subjective-chief-complaint"
                      />
                    }
                  />
                )}
              />
            )}
          </Box>
        )}

        {showHpi && (
          <Box>
            <NoteHeading level="sub" id="hpi" label="History of Present Illness (HPI)">
              <Text
                component="span"
                c="dimmed"
                size="xs"
                ml={6}
                style={{ fontFamily: MONO_FONT, letterSpacing: 0 }}
              >
                LOINC 10164-2
              </Text>
            </NoteHeading>
            {readOnly ? (
              hpiParagraphs.length === 0 ? (
                <Text size="sm" c="dark.7" style={{ lineHeight: 1.7 }}>
                  —
                </Text>
              ) : (
                <Stack gap="sm">
                  {hpiParagraphs.map((para, idx) => (
                    <Text
                      key={para?.id ?? idx}
                      size="sm"
                      c="dark.7"
                      style={{ lineHeight: 1.7, whiteSpace: 'pre-wrap' }}
                    >
                      {para?.text || '—'}
                    </Text>
                  ))}
                </Stack>
              )
            ) : (
              <Stack gap="xs">
                {hpiFieldArray.fields.map((field, idx) => (
                  <Box key={field.id} style={{ position: 'relative' }}>
                    <Controller
                      control={control}
                      name={`summary.hpi.${idx}.text`}
                      render={({ field: f }) => (
                        <Textarea
                          autosize
                          minRows={3}
                          placeholder="HPI paragraph"
                          value={f.value}
                          onChange={(e) => f.onChange(e.currentTarget.value)}
                          onBlur={f.onBlur}
                          data-testid={`subjective-hpi-${idx}`}
                          // Pad the right side so the trash icon never overlaps text.
                          styles={{ input: { paddingRight: 36 } }}
                        />
                      )}
                    />
                    {hpiFieldArray.fields.length > 1 && (
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        size="sm"
                        aria-label="Remove HPI paragraph"
                        onClick={() => hpiFieldArray.remove(idx)}
                        style={{ position: 'absolute', top: 6, right: 6 }}
                      >
                        <IconTrash size={14} />
                      </ActionIcon>
                    )}
                    <div style={{ position: 'absolute', top: 6, right: 32 }}>
                      <DictateFieldMic
                        field={{
                          path: `summary.hpi.${idx}.text`,
                          label: 'History of Present Illness',
                        }}
                        testId={`subjective-hpi-${idx}`}
                      />
                    </div>
                  </Box>
                ))}
                <Button
                  size="xs"
                  variant="light"
                  color="blue"
                  leftSection={<IconPlus size={14} />}
                  onClick={() => hpiFieldArray.append({ id: crypto.randomUUID(), text: '' })}
                  data-testid="subjective-hpi-add"
                  style={{ alignSelf: 'flex-start' }}
                >
                  Add Paragraph
                </Button>
              </Stack>
            )}
          </Box>
        )}

        {showRos && (
          <Box>
            <NoteHeading level="sub" id="review-of-systems" label="Review of Systems" />
            {rosRows.length === 0 && readOnly ? (
              <Text size="xs" c="dimmed">
                No review of systems recorded.
              </Text>
            ) : (
              <Table withTableBorder withColumnBorders striped="even">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ width: 200 }}>System</Table.Th>
                    <Table.Th>Finding</Table.Th>
                    {!readOnly && <Table.Th style={{ width: 48 }} aria-label="actions" />}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {rosFieldArray.fields.map((field, idx) => (
                    <Table.Tr key={field.id} data-testid={`ros-row-${idx}`}>
                      <Table.Td style={{ verticalAlign: 'top', fontWeight: 600 }}>
                        {readOnly ? (
                          rosRows[idx]?.system || '—'
                        ) : (
                          <Controller
                            control={control}
                            name={`summary.ros.${idx}.system`}
                            render={({ field: f }) => (
                              <TextInput
                                size="xs"
                                value={f.value}
                                onChange={(e) => f.onChange(e.currentTarget.value)}
                                onBlur={f.onBlur}
                                placeholder="e.g. Constitutional"
                              />
                            )}
                          />
                        )}
                      </Table.Td>
                      <Table.Td style={{ verticalAlign: 'top' }}>
                        {readOnly ? (
                          <Text size="sm" c="dark.6" style={{ lineHeight: 1.6 }}>
                            {rosRows[idx]?.finding || '—'}
                          </Text>
                        ) : (
                          <Controller
                            control={control}
                            name={`summary.ros.${idx}.finding`}
                            render={({ field: f }) => (
                              <TextInput
                                size="xs"
                                value={f.value}
                                onChange={(e) => f.onChange(e.currentTarget.value)}
                                onBlur={f.onBlur}
                                placeholder="e.g. Denies fever or chills"
                              />
                            )}
                          />
                        )}
                      </Table.Td>
                      {!readOnly && (
                        <Table.Td style={{ verticalAlign: 'top', textAlign: 'center' }}>
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            size="sm"
                            aria-label="Remove ROS row"
                            onClick={() => rosFieldArray.remove(idx)}
                          >
                            <IconTrash size={14} />
                          </ActionIcon>
                        </Table.Td>
                      )}
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
            {!readOnly && (
              <Button
                size="xs"
                variant="light"
                color="blue"
                mt="xs"
                leftSection={<IconPlus size={14} />}
                onClick={() => rosFieldArray.append({ system: '', finding: '' })}
                data-testid="ros-add-row"
              >
                Add Row
              </Button>
            )}
          </Box>
        )}
      </Stack>
    </Box>
  )
}
